/**
 * Subdomain Router for NoonX Platform
 * Handles routing based on subdomain (admin.noonx.in, brand.noonx.in, creator.noonx.in, agency.noonx.in)
 */

export type SubdomainType = 'admin' | 'brand' | 'creator' | 'main';

/**
 * Get the current subdomain from the hostname
 */
export function getSubdomain(): SubdomainType {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check URL params for testing subdomains locally
    const params = new URLSearchParams(window.location.search);
    const testSubdomain = params.get('subdomain');
    if (testSubdomain && ['admin', 'brand', 'creator'].includes(testSubdomain)) {
      return testSubdomain as SubdomainType;
    }
    return 'main';
  }
  
  // Production subdomains
  if (hostname.startsWith('admin.')) return 'admin';
  if (hostname.startsWith('brand.')) return 'brand';
  if (hostname.startsWith('creator.')) return 'creator';
  
  return 'main';
}

/**
 * Get the default route for a subdomain
 */
export function getDefaultRoute(subdomain: SubdomainType): string {
  switch (subdomain) {
    case 'admin':
      return '/admin-dashboard';
    case 'brand':
      return '/brand-dashboard';
    case 'creator':
      return '/creator-dashboard';
    default:
      return '/';
  }
}

/**
 * Get the login redirect route for a subdomain
 */
export function getLoginRedirect(subdomain: SubdomainType): string {
  switch (subdomain) {
    case 'admin':
      return '/admin-dashboard';
    case 'brand':
      return '/brand-dashboard';
    case 'creator':
      return '/creator-dashboard';
    default:
      return '/authentication';
  }
}

/**
 * Check if current subdomain matches user role
 */
export function isCorrectSubdomainForRole(subdomain: SubdomainType, userRole: string): boolean {
  switch (subdomain) {
    case 'admin':
      return userRole === 'admin';
    case 'brand':
      return userRole === 'brand';
    case 'creator':
      return userRole === 'creator';
    case 'main':
      return true; // Main domain accepts all roles
    default:
      return true;
  }
}

/**
 * Get the correct subdomain URL for a user role
 */
export function getSubdomainUrl(role: string): string {
  const protocol = window.location.protocol;
  const baseDomain = getBaseDomain();
  
  switch (role) {
    case 'admin':
      return `${protocol}//admin.${baseDomain}`;
    case 'brand':
      return `${protocol}//brand.${baseDomain}`;
    case 'creator':
      return `${protocol}//creator.${baseDomain}`;
    default:
      return `${protocol}//${baseDomain}`;
  }
}

/**
 * Get the base domain (e.g., noonx.in from admin.noonx.in)
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${hostname}:${window.location.port}`;
  }
  
  // Remove subdomain prefix
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts.slice(1).join('.');
  }
  
  return hostname;
}

/**
 * Redirect to correct subdomain based on user role
 */
export function redirectToCorrectSubdomain(userRole: string): void {
  const currentSubdomain = getSubdomain();
  
  if (!isCorrectSubdomainForRole(currentSubdomain, userRole)) {
    const correctUrl = getSubdomainUrl(userRole);
    window.location.href = correctUrl;
  }
}
